import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, Search, ChevronLeft, ChevronRight, Check, X as XIcon,
  PencilLine, RotateCcw, Filter,
} from "lucide-react";

import {
  fetchMastersDimensions,
  fetchMastersEditions,
  type EditionRow,
} from "../api/masters";
import { explainError } from "../api/client";
import { Spinner } from "../components/Spinner";
import { EditionEditDrawer } from "../components/EditionEditDrawer";
import { shortDate } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   Masters → Editions
   ──────────────────────────────────────────────────────────────────────
   Paginated table over crawler.v_EditionMaster_Enriched. Filters: text
   search, category, language, IsActive, IsCrawlable, NeedsReview, "has
   NULL columns." Inline edit for City / Edition name / IsActive / IsCrawlable
   via a side drawer. Clearing the review flag is one click in the drawer.
   ────────────────────────────────────────────────────────────────────── */

interface UiFilters {
  q:           string;
  categoryId:  number | "";
  languageId:  number | "";
  isActive:    "any" | "true" | "false";
  isCrawlable: "any" | "true" | "false";
  needsReview: "any" | "true" | "false";
  hasNulls:    boolean;
}

const PAGE_SIZE = 50;

function asBool(v: "any" | "true" | "false"): boolean | undefined {
  return v === "any" ? undefined : v === "true";
}

export function MastersEditions() {
  /* filters ------------------------------------------------------ */
  const [filters, setFilters] = useState<UiFilters>({
    q: "",
    categoryId: "",
    languageId: "",
    isActive:    "any",
    isCrawlable: "any",
    needsReview: "any",
    hasNulls:    false,
  });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");   // committed `q` value
  const [editing, setEditing] = useState<EditionRow | null>(null);

  const setF = useCallback(<K extends keyof UiFilters>(k: K, v: UiFilters[K]) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  }, []);

  /* queries ------------------------------------------------------ */
  const dims = useQuery({
    queryKey: ["masters/dimensions"],
    queryFn:  fetchMastersDimensions,
    staleTime: 5 * 60_000,
  });

  const editions = useQuery({
    queryKey: ["masters/editions", page, search, filters],
    queryFn: () => fetchMastersEditions({
      page,
      pageSize:   PAGE_SIZE,
      q:          search || undefined,
      categoryId: filters.categoryId || undefined,
      languageId: filters.languageId || undefined,
      isActive:    asBool(filters.isActive),
      isCrawlable: asBool(filters.isCrawlable),
      needsReview: asBool(filters.needsReview),
      hasNulls:    filters.hasNulls || undefined,
      sort: filters.needsReview === "true" ? "review" : "channel_name",
    }),
  });

  const total      = editions.data?.total ?? 0;
  const counts     = editions.data?.counts ?? {};
  const rows       = editions.data?.rows  ?? [];
  const pageCount  = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function resetFilters() {
    setFilters({
      q: "", categoryId: "", languageId: "",
      isActive: "any", isCrawlable: "any", needsReview: "any",
      hasNulls: false,
    });
    setSearch("");
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-3 animate-fade-up">
      <header className="flex items-baseline justify-between flex-wrap gap-2 pb-2 border-b"
              style={{ borderColor: "var(--rule)" }}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <h1 className="font-display text-[22px] font-semibold tracking-tight leading-none">
            Masters · Editions
          </h1>
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">·</span>
          <span className="font-mono text-[11px] tabular-nums text-[var(--fg-muted)]">
            {editions.isLoading
              ? "loading…"
              : `${total.toLocaleString()} rows  ·  page ${page}/${pageCount}`}
          </span>
        </div>
      </header>

      {/* filter ribbon */}
      <section
        className="np-card-inset px-3 py-2 flex flex-wrap items-center gap-2"
        style={{ borderColor: "var(--rule)" }}
      >
        <Filter size={12} className="text-[var(--fg-muted)] shrink-0" />

        <form
          className="flex items-center gap-1 border px-2"
          style={{ borderColor: "var(--rule)", background: "var(--bg-card)" }}
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(filters.q.trim());
            setPage(1);
          }}
        >
          <Search size={11} className="text-[var(--fg-faint)]" />
          <input
            type="text"
            value={filters.q}
            placeholder="search channel / city / edition"
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="bg-transparent outline-none text-[12px] font-sans py-1 w-56"
          />
          {filters.q && (
            <button
              type="button"
              onClick={() => { setFilters({ ...filters, q: "" }); setSearch(""); }}
              className="text-[var(--fg-faint)] hover:text-[var(--fg)]"
              aria-label="Clear search"
            >
              <XIcon size={11} />
            </button>
          )}
        </form>

        <SelectFilter
          label="Category"
          value={String(filters.categoryId)}
          onChange={(v) => setF("categoryId", v ? Number(v) : "")}
          options={[
            { v: "", l: "All" },
            ...((dims.data?.categories ?? []).map(c => ({
              v: String(c.id),
              l: `${c.name} (${c.edition_count})`,
            }))),
          ]}
        />

        <SelectFilter
          label="Language"
          value={String(filters.languageId)}
          onChange={(v) => setF("languageId", v ? Number(v) : "")}
          options={[
            { v: "", l: "All" },
            ...((dims.data?.languages ?? []).map(l => ({
              v: String(l.id),
              l: `${l.name} (${l.edition_count})`,
            }))),
          ]}
        />

        <ToggleFilter label="Active"   value={filters.isActive}    onChange={(v) => setF("isActive", v)} />
        <ToggleFilter label="Crawler"  value={filters.isCrawlable} onChange={(v) => setF("isCrawlable", v)} />
        <ToggleFilter label="Review"   value={filters.needsReview} onChange={(v) => setF("needsReview", v)} />

        <label className="inline-flex items-center gap-1.5 px-2 py-1 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.hasNulls}
            onChange={(e) => setF("hasNulls", e.target.checked)}
            className="accent-[var(--accent)]"
          />
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)]">
            Has nulls
          </span>
        </label>

        <div className="flex-1" />

        <div className="flex items-center gap-1.5 font-mono text-[10px] tabular-nums text-[var(--fg-muted)]">
          {counts.needs_review !== undefined && counts.needs_review > 0 && (
            <span className="text-[var(--accent)]">
              <AlertTriangle size={10} className="inline mr-1" />
              {counts.needs_review} need review
            </span>
          )}
          {counts.crawlable !== undefined && (
            <span>· {counts.crawlable} crawlable</span>
          )}
          {counts.inactive !== undefined && counts.inactive > 0 && (
            <span>· {counts.inactive} inactive</span>
          )}
          {counts.missing_city !== undefined && counts.missing_city > 0 && (
            <span>· {counts.missing_city} no city</span>
          )}
        </div>

        <button onClick={resetFilters} className="np-btn-ghost text-[10px]" title="Reset all filters">
          <RotateCcw size={10} /> Reset
        </button>
      </section>

      {/* table */}
      {editions.isLoading && (
        <div className="py-8 flex justify-center"><Spinner label="Loading editions" /></div>
      )}

      {editions.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio mr-2 text-[var(--accent)]">Error</span>
          Failed to load editions — {explainError(editions.error)}
        </div>
      )}

      {editions.data && (
        <section className="np-card overflow-hidden">
          {/* header */}
          <div
            className="grid grid-cols-[44px,1fr,90px,80px,80px,82px,72px,56px,52px,84px,22px] gap-2 px-3 py-2 border-b
                       font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]"
            style={{ borderColor: "var(--rule)" }}
          >
            <div className="text-right">EdId</div>
            <div>Publisher · City</div>
            <div>Category</div>
            <div>Language</div>
            <div>State</div>
            <div className="text-right" title="Issues last 30 days">Iss(30d)</div>
            <div className="text-right" title="Most recent business date">Last</div>
            <div className="text-right" title="IsCrawlable">Crawl</div>
            <div className="text-right" title="IsActive">Act</div>
            <div>Review</div>
            <div />
          </div>

          <ul>
            {rows.map((r) => (
              <li
                key={r.edition_id}
                className="border-b transition-colors hover:bg-[var(--bg-inset)] last:border-b-0"
                style={{ borderColor: "var(--rule)" }}
              >
                <button
                  type="button"
                  onClick={() => setEditing(r)}
                  className="w-full grid grid-cols-[44px,1fr,90px,80px,80px,82px,72px,56px,52px,84px,22px] gap-2 items-baseline px-3 py-2 text-left"
                >
                  <div className="text-right font-mono text-[10px] tabular-nums text-[var(--fg-faint)]">
                    {r.edition_id}
                  </div>

                  <div className="min-w-0 flex flex-col">
                    <div className="font-display text-[14px] leading-tight truncate">
                      {r.channel_name}
                      {!r.is_active && (
                        <span className="ml-1.5 font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]">
                          inactive
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-[var(--fg-muted)] truncate">
                      {r.city
                        ? `${r.city}${r.edition && r.edition !== r.channel_name ? ` · ${r.edition}` : ""}`
                        : <span className="text-[var(--accent)]">no city</span>}
                    </div>
                  </div>

                  <div className="font-mono text-[11px] text-[var(--fg-muted)] truncate">
                    {r.category || <span className="text-[var(--accent)]">—</span>}
                  </div>

                  <div className="font-mono text-[11px] text-[var(--fg-muted)] truncate">
                    {r.language || <span className="text-[var(--accent)]">—</span>}
                  </div>

                  <div className="font-mono text-[11px] text-[var(--fg-muted)] truncate">
                    {r.state || "—"}
                  </div>

                  <div className="text-right font-mono text-[12px] tabular-nums">
                    {r.issues_last_30 > 0
                      ? <span className="text-[var(--fg)]">{r.issues_last_30}</span>
                      : <span className="text-[var(--fg-faint)]">—</span>}
                  </div>

                  <div className="text-right font-mono text-[11px] text-[var(--fg-muted)]">
                    {r.last_issue_date ? shortDate(r.last_issue_date) : <span className="text-[var(--fg-faint)]">—</span>}
                  </div>

                  <div className="text-right">
                    {r.is_crawlable
                      ? <Check size={12} className="inline text-[var(--primary)]" aria-label="crawlable" />
                      : <XIcon size={12} className="inline text-[var(--fg-faint)]" aria-label="not crawlable" />}
                  </div>

                  <div className="text-right">
                    {r.is_active
                      ? <Check size={12} className="inline text-[var(--primary)]" />
                      : <XIcon size={12} className="inline text-[var(--fg-faint)]" />}
                  </div>

                  <div>
                    {r.needs_review ? (
                      <span title={r.review_reason ?? ""}
                            className="font-mono text-[9px] uppercase tracking-folio px-1.5 py-0.5"
                            style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>
                        review
                      </span>
                    ) : <span className="text-[var(--fg-faint)] text-[11px]">—</span>}
                  </div>

                  <div className="text-right">
                    <PencilLine size={12} className="inline text-[var(--fg-faint)]" />
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {/* pager */}
          <div className="px-3 py-2 border-t flex items-center justify-between font-mono text-[11px] text-[var(--fg-muted)]"
               style={{ borderColor: "var(--rule)" }}>
            <span>
              showing {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + rows.length} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="np-btn-ghost p-1.5 disabled:opacity-30"
                aria-label="Previous page"
              >
                <ChevronLeft size={12} />
              </button>
              <span className="px-2 tabular-nums">{page} / {pageCount}</span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className="np-btn-ghost p-1.5 disabled:opacity-30"
                aria-label="Next page"
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        </section>
      )}

      {editing && (
        <EditionEditDrawer
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/* ── small filter primitives ───────────────────────────────────────── */
function SelectFilter({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { v: string; l: string }[];
}) {
  return (
    <label className="inline-flex items-center gap-1.5 border px-2 py-1"
           style={{ borderColor: "var(--rule)", background: "var(--bg-card)" }}>
      <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-0 outline-none font-mono text-[11px] text-[var(--fg)]"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}

function ToggleFilter({
  label, value, onChange,
}: {
  label: string;
  value: "any" | "true" | "false";
  onChange: (v: "any" | "true" | "false") => void;
}) {
  return (
    <div className="inline-flex border" style={{ borderColor: "var(--rule)", background: "var(--bg-card)" }}>
      <span className="px-2 py-1 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] border-r"
            style={{ borderColor: "var(--rule)" }}>{label}</span>
      {(["any", "true", "false"] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className="px-2 py-1 font-mono text-[10px] uppercase tracking-folio"
          style={{
            color: value === v ? "var(--fg)" : "var(--fg-muted)",
            background: value === v ? "var(--bg-inset)" : "transparent",
            borderRight: v !== "false" ? "1px solid var(--rule)" : undefined,
          }}
        >
          {v === "any" ? "·" : v === "true" ? "yes" : "no"}
        </button>
      ))}
    </div>
  );
}

